import { ScholarshipCard, ScholarshipSort, SortDirection } from '@common-types';

import { MainFilters } from '@store/AllScholarshipsStore';

import { FILTERS_QUERY_DATA, FILTERS_TYPES } from '@constants/allScholarships';

import { Option } from '@interfaces';

import { SortByValues } from '@pages/Scholarships/components/SortBy';

export const getQueryParams = (filters: MainFilters) => {
  const params: Record<string, unknown> = {
    [FILTERS_TYPES.falseSearchFields]: [],
    [FILTERS_TYPES.trueSearchFields]: [],
    [FILTERS_TYPES.stateResidence]: [],
    [FILTERS_TYPES.areasOfStudy]: [],
  };

  Object.entries(filters).forEach(([key, value]) => {
    if (!value || !FILTERS_QUERY_DATA[key]?.type) return;

    if (FILTERS_QUERY_DATA[key]?.type === FILTERS_TYPES.boolean) {
      params[key] = value;
    } else if (FILTERS_QUERY_DATA[key]?.type === FILTERS_TYPES.number) {
      params[key] = value;
    } else if (FILTERS_QUERY_DATA[key]?.type === FILTERS_TYPES.moneyRange) {
      params.moneyLowerLimit = value[0];
      params.moneyUpperLimit = value[1];
    } else if (FILTERS_QUERY_DATA[key]?.type === FILTERS_TYPES.string) {
      params[key] = value;
    } else if (FILTERS_QUERY_DATA[key]?.type === FILTERS_TYPES.stateResidence) {
      const queryValuesArr = (value as Option[]).reduce(
        (accumulator, { label, value }) => [...accumulator, value],
        [] as string[],
      );

      params[key] = queryValuesArr;
    } else {
      (params[FILTERS_QUERY_DATA[key]?.type] as string[])?.push(FILTERS_QUERY_DATA[key].value!);
    }
  });

  Object.keys(params).forEach((key) => {
    if (!Array.isArray(params[key])) return;

    if (!(params[key] as string[]).length) {
      delete params[key];

      return;
    }

    params[key] = (params[key] as string[]).join(',');
  });

  return params;
};

const sortByDeadline = (list: ScholarshipCard[], sortValues: SortByValues) => {
  const scholarshipsWithDeadline = [] as ScholarshipCard[];
  const scholarshipsWithoutDeadline = [] as ScholarshipCard[];

  list?.forEach((scholarship) => {
    if (Object.values(scholarship.applicationDate).some((item) => !item)) {
      scholarshipsWithoutDeadline.push(scholarship);
    } else {
      scholarshipsWithDeadline.push(scholarship);
    }
  });

  const sortedScholarshipsWithoutDeadline = scholarshipsWithoutDeadline.sort((a, b) =>
    a.programName.localeCompare(b.programName),
  );

  const earliestOrderBy = [
    ...scholarshipsWithDeadline.sort((a, b) => {
      const preparedDateA = new Date(
        a.applicationDate.deadlineYear ?? 0,
        a.applicationDate.deadlineMonth ?? 0,
        a.applicationDate.deadlineDay ?? 0,
      ).toString();

      const preparedDateB = new Date(
        b.applicationDate.deadlineYear ?? 0,
        b.applicationDate.deadlineMonth ?? 0,
        b.applicationDate.deadlineDay ?? 0,
      ).toString();

      return Date.parse(preparedDateA) - Date.parse(preparedDateB);
    }),
    ...sortedScholarshipsWithoutDeadline,
  ];

  const latestOrderBy = [
    ...scholarshipsWithDeadline.sort((a, b) => {
      const preparedDateA = new Date(
        a.applicationDate.deadlineYear ?? 0,
        a.applicationDate.deadlineMonth ?? 0,
        a.applicationDate.deadlineDay ?? 0,
      ).toString();
      const preparedDateB = new Date(
        b.applicationDate.deadlineYear ?? 0,
        b.applicationDate.deadlineMonth ?? 0,
        b.applicationDate.deadlineDay ?? 0,
      ).toString();

      return Date.parse(preparedDateB) - Date.parse(preparedDateA);
    }),
    ...sortedScholarshipsWithoutDeadline,
  ];

  if (sortValues.sortDirection === SortDirection.DESC) {
    return latestOrderBy;
  }
  return earliestOrderBy;
};

const sortByAward = (list: ScholarshipCard[], sortValues: SortByValues) => {
  const scholarshipsWithMoneyRange = [] as ScholarshipCard[];
  const scholarshipsWithoutMoneyRange = [] as ScholarshipCard[];

  list?.forEach((scholarship) => {
    if (scholarship.moneyRange.lowerLimit) {
      scholarshipsWithMoneyRange.push(scholarship);
    } else {
      scholarshipsWithoutMoneyRange.push(scholarship);
    }
  });

  const sortedScholarshipsWithoutMoneyRange = scholarshipsWithoutMoneyRange.sort((a, b) =>
    a.programName.localeCompare(b.programName),
  );

  const moneyRangeSort = (a: ScholarshipCard, b: ScholarshipCard) => {
    const lowerLimitA = a.moneyRange.lowerLimit as number;
    const lowerLimitB = b.moneyRange.lowerLimit as number;

    return lowerLimitA - lowerLimitB;
  };

  const lowestOrderBy = [...scholarshipsWithMoneyRange.sort(moneyRangeSort), ...sortedScholarshipsWithoutMoneyRange];

  const highestOrderBy = [
    ...scholarshipsWithMoneyRange.sort(moneyRangeSort).reverse(),
    ...sortedScholarshipsWithoutMoneyRange,
  ];

  if (sortValues.sortDirection === SortDirection.DESC) {
    return highestOrderBy;
  }
  return lowestOrderBy;
};

export const getSortedScholarships = (list: ScholarshipCard[], sortValues: SortByValues) => {
  if (sortValues.sort === ScholarshipSort.DEADLINE) {
    return sortByDeadline(list, sortValues);
  }

  if (sortValues.sort === ScholarshipSort.AWARD) {
    return sortByAward(list, sortValues);
  }

  if (sortValues.sortDirection === SortDirection.DESC) {
    return list.sort((a, b) => b.programName.localeCompare(a.programName));
  }
  return list.sort((a, b) => a.programName.localeCompare(b.programName));
};
